const PROD_API = 'https://api.ohbellezaoficial.com';
const RAW_API = String(process.env.REACT_APP_API_URL || '').trim().replace(/\/$/, '');
const API_BASE = RAW_API || (process.env.NODE_ENV === 'production' ? PROD_API : '');

function connectJitterMs() {
  return 3000 + Math.floor(Math.random() * 12000);
}

/** SSE con jitter inicial y backoff largo (hasta ~3 min) — evita storm al abrir 11 clientes. */
export function connectRealtime({
  app,
  getToken,
  onInvalidate,
  onReconnect,
  debounceMs = 400,
} = {}) {
  let closed = false;
  let es = null;
  let retryMs = 15000;
  let debounceTimer = null;
  let startTimer = null;
  let retryTimer = null;
  const pending = new Set();
  let pendingMeta = {};
  let reconnectHookPending = false;

  function flush() {
    const domains = [...pending];
    const meta = { ...pendingMeta };
    pending.clear();
    pendingMeta = {};
    if (!domains.length) return;
    const detail = { domains, ...meta, app };
    try {
      window.dispatchEvent(new CustomEvent('oh:invalidate', { detail }));
    } catch {
      /* ignore */
    }
    onInvalidate?.(domains, detail);
  }

  function queueInvalidate(data) {
    (data?.domains || []).forEach((d) => pending.add(d));
    if (data?.reason) pendingMeta.reason = data.reason;
    if (data?.codigo) pendingMeta.codigo = data.codigo;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(flush, debounceMs);
  }

  function connect() {
    if (closed) return;
    try {
      es?.close();
    } catch {
      /* ignore */
    }
    const params = new URLSearchParams();
    if (app) params.set('app', app);
    const token = typeof getToken === 'function' ? getToken() : '';
    if (token) params.set('token', token);
    const url = `${API_BASE}/api/events?${params.toString()}`;
    es = new EventSource(url);

    es.addEventListener('ready', () => {
      retryMs = 15000;
      if (reconnectHookPending) {
        reconnectHookPending = false;
        onReconnect?.();
      }
    });

    es.addEventListener('invalidate', (ev) => {
      try {
        queueInvalidate(JSON.parse(ev.data || '{}'));
      } catch {
        /* ignore */
      }
    });

    es.onerror = () => {
      try {
        es.close();
      } catch {
        /* ignore */
      }
      if (closed) return;
      reconnectHookPending = true;
      const wait = retryMs + Math.floor(Math.random() * 5000);
      retryMs = Math.min(Math.round(retryMs * 1.7), 180000);
      clearTimeout(retryTimer);
      retryTimer = setTimeout(connect, wait);
    };
  }

  function forceReconnect() {
    if (closed) return;
    retryMs = Math.max(retryMs, 15000);
    reconnectHookPending = true;
    clearTimeout(retryTimer);
    connect();
  }

  startTimer = setTimeout(connect, connectJitterMs());

  const onOnline = () => forceReconnect();
  const onVis = () => {
    if (document.visibilityState !== 'visible') return;
    if (!es || es.readyState !== EventSource.OPEN) forceReconnect();
  };
  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVis);

  return () => {
    closed = true;
    clearTimeout(debounceTimer);
    clearTimeout(startTimer);
    clearTimeout(retryTimer);
    try {
      es?.close();
    } catch {
      /* ignore */
    }
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVis);
  };
}

export function bootStaggerMs(maxMs = 8000) {
  return Math.floor(Math.random() * Math.max(0, maxMs));
}

/** Avisar a esta pestaña del Admin (y otras apps abiertas vía SSE) que cambió el catálogo. */
export function broadcastInvalidate(detail = {}) {
  const domains = detail.domains?.length ? detail.domains : ['product'];
  try {
    window.dispatchEvent(new CustomEvent('oh:invalidate', { detail: { ...detail, domains } }));
  } catch {
    /* ignore */
  }
}
